import { useQueryClient } from '@tanstack/react-query';
import type { DemandeLotOeuvres, RapportLotOeuvres } from '@artosera/shared';
import { useMutation } from './mutation.js';
import { api, type AppelApi } from './client.js';
import { cleMetier, dansLaPortee } from './cles.js';
import { requeteFiltres, type FiltresOeuvres } from './oeuvres.js';

/**
 * Modification groupée des œuvres — `POST …/oeuvres/lot` et
 * `GET …/oeuvres/identifiants` (`docs/dev/lot-oeuvres.md`). Le contrat est
 * celui de `@artosera/shared` (`lot-oeuvres.ts`), partagé avec l'API et iOS.
 */

export interface IdentifiantsRecherche {
  readonly ids: readonly string[];
  readonly total: number;
  readonly plafond: number;
  readonly tronque: boolean;
}

export const lotOeuvresApi = {
  /** Les œuvres de toute la recherche filtrée, au-delà de la page, jusqu'au plafond. */
  identifiants: (galerie: string, filtres: FiltresOeuvres, appel?: AppelApi) => {
    const params = new URLSearchParams(requeteFiltres(filtres));
    for (const cle of ['page', 'parPage', 'vignettes']) params.delete(cle);
    return api.get<IdentifiantsRecherche>(
      `/g/${encodeURIComponent(galerie)}/oeuvres/identifiants?${params.toString()}`,
      { galerie, ...appel },
    );
  },
  /**
   * Aperçu (`essai: true`) ou écriture. `cleOperation` rend l'écriture
   * rejouable sans effet double : la même clé rend le même lot.
   */
  executer: (galerie: string, demande: DemandeLotOeuvres, appel?: AppelApi) =>
    api.post<RapportLotOeuvres>(`/g/${encodeURIComponent(galerie)}/oeuvres/lot`, demande, {
      galerie,
      ...appel,
    }),
};

/** L'aperçu : rien n'est écrit, aucune invalidation. */
export function useApercuLot(galerie: string) {
  return useMutation(
    dansLaPortee({
      mutationFn: (demande: Omit<DemandeLotOeuvres, 'essai' | 'empreinteApercu'>, appel) =>
        lotOeuvresApi.executer(galerie, { ...demande, essai: true }, appel),
    }),
  );
}

/**
 * La confirmation : l'empreinte de l'aperçu relu, et une clé d'opération
 * tirée UNE fois par confirmation (un nouvel essai après coupure réseau
 * renvoie la même, et ne refait pas le lot).
 */
export function useAppliquerLot(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (
        {
          demande,
          empreinteApercu,
          cleOperation,
        }: {
          demande: Omit<DemandeLotOeuvres, 'essai' | 'empreinteApercu'>;
          empreinteApercu: string;
          cleOperation: string;
        },
        appel,
      ) =>
        lotOeuvresApi.executer(
          galerie,
          { ...demande, essai: false, empreinteApercu },
          { ...appel, cleOperation },
        ),
      onSuccess: () => {
        for (const domaine of ['oeuvres', 'expositions', 'presentation'] as const) {
          void client.invalidateQueries({ queryKey: cleMetier(galerie, domaine) });
        }
      },
    }),
  );
}
